import { Fabric, initializeIcons, registerIcons, IconButton, Stack, PrimaryButton, Spinner } from "office-ui-fabric-react";
import { cloneDeep, initial } from "lodash";
import * as React from "react";
import { createDeskSvg, createDoorSvg } from "../SvgElements";
import { IDesk, IMouseDownOperation, IRoomRectangle, IStampable, ISvgCurve, ISvgShape, ISvgStart, ITextBox, MouseDownOperationType, StampObj, SvgShapeComponent, TBFontSize, ToolType } from "../../../types/Floorplan.types";
import Desks from "../Desks";
import ZoomControls from "../ZoomControls";
import DeskCanvasControls from "./DeskCanvasControls";
import RoomEditorTools from "./RoomEditorTools";
import Doors from "../Doors";
import DoorCanvasControls from "./DoorCanvasControls";
import TextElements from "../TextElements";
import { TextEditorPanel } from "./TextEditorPanel";
import { loadLatestFloorplanEdit, publishFloorplanLayout, updateFloorplanLayout } from "../../../api/floorplans";
import { IFloorplanLayout } from "../../../types/SiteLocation.types";
import { DeskEditorPanel } from "./DeskEditorPanel";
import { RouteComponentProps, withRouter } from "react-router";
import { isAuthenticated } from "../../../api/userInfo";
import { DeskCheckDialog } from "../DeskCheckDialog";
import { CancelBookingsPanel } from "../CancelBookingsPanel";
import FloorRects from "../FloorRects";
import RoomRects from "../RoomRects";
import SvgShapes from "../CustomShapes";
import PreviewShape from "../PreviewShape";
import ShapeCanvasControls from "./ShapeCanvasControls";
import RectCanvasControls from "./FloorRectCanvasControls";

export interface IRoomEditorProps extends RouteComponentProps {
    floorplanId: string;
    layoutId: number;
}

export interface IRoomEditorDrawingState {
    desks: IDesk[];
    doors: IStampable[];
    text: ITextBox[];
    roomRectangles: IRoomRectangle[];
    floorplanRectangles: IRoomRectangle[];
    floorplanShapes:ISvgShape[];
    tempShape: SvgShapeComponent[];
    lastUpdate?: string;
}

export interface IRoomEditorState {
    lastSave?: Date;
    saving: boolean;
    /** Current drawing state including desks, floorplan rectangles and room rectangles */
    drawing: IRoomEditorDrawingState;
    /** Determines whether the Room/Rectangle draw operation will draw mergable floorplan rectangles or regular rooms/boxes */
    drawInternalRooms: boolean;
    /** Determines whether the Desk tool will place a bookable desk or a non-bookable desk */
    availability: boolean;
    /** Determines the rotation of a desk when first placed */
    defaultDeskRotation: number;
    /** Stores drawing state following actions that have been undone */
    redoStates: IRoomEditorDrawingState[];
    /** Index of the selected desk in the drawing.desks array. Populated by clicking a desk using the Pointer tool. */
    selectedDeskIdx?: number;
    /** Index of the desk element being edited in the drawing.desks array. Populated by double clicking a desk using the Pointer tool. */
    editingDeskIdx?: number;
    /** Index of the selected desk in the drawing.doors array. Populated by clicking a door using the Pointer tool. */
    selectedDoorIdx?: number;
    /** Index of the selected text element in the drawing.text array. Populated by adding new text or clicking a text element */
    selectedTextIdx?: number;
    /** Index of the selected room element in the drawing.roomRectangles array. Populated by clicking a room using the Pointer tool */
    selectedRoomIdx?: number;
    /** Index of the selected flooring element in the drawing.floorplanRectangles array. Populated by clicking a floor using the Pointer tool */
    selectedFloorIdx?: number;
    /** Index of the selected flooring shape element in the drawing.floorplanShapes array. Populated by clicking a floor using the Pointer tool */
    selectedShapeIdx?: number;
    /** Index of the text element being edited in the drawing.text array. Populated by adding new text or double clicking a text element */
    editingTextIdx?: number;
    /** The currently selected editor tool */
    selectedTool: ToolType;
    /** Determines whether gridlines will be shown on the canvas */
    showGrid: boolean;
    /** Determines whether drawing operations will be restricted to 10px units */
    snapToGrid: boolean;
    /** Stores historical drawing state to allow reverting recently performed actions */
    undoStates: IRoomEditorDrawingState[];
    /** The zoom level of the drawing. Used to calculate a viewBox for the SVG. */
    zoom: number;
    /** Determines whether user is attempting to delete a desk from the floorplan */
    deletingDeskId?: number;
    /** Determines whether bookings panel it to be shown */
    showBookingsPanel?: boolean;
}

const xmlns = "http://www.w3.org/2000/svg";

export class RoomEditor extends React.Component<IRoomEditorProps, IRoomEditorState> {
    
    private _initialDrawingState: IRoomEditorDrawingState = { 
        desks: [],
        doors: [],
        text: [],
        floorplanRectangles: [], 
        floorplanShapes: [],
        tempShape: [],
        roomRectangles: []
    };

    private _autoSaveTimer: number|undefined;

    /** Reference to the Room Editor's SVG Canvas */
    private svg: SVGSVGElement | null = null;
    /** Live mouse cursor X position - used in some drawing operations during MouseMove */
    private _liveX: number = 0; 
    /** Live mouse cursor Y position - used in some drawing operations during MouseMove */
    private _liveY: number = 0;
    /** Editor's current MouseDown operation. This determines how the editor will behave during MouseMove and on MouseUp. */
    private _mouseDownOperation: IMouseDownOperation | null = null;
    /** Tells the editor if the pen is in use. Mouse move, mouse down and mouse up behaviour changes based on this */
    private _penInUse: boolean = false;

    constructor(props: IRoomEditorProps) {
        super(props);

        this._setupIcons();

        this._publish = this._publish.bind(this);
        this._autoSave = this._autoSave.bind(this);
        this._loadLayout = this._loadLayout.bind(this);
        this._checkLocalDrafts = this._checkLocalDrafts.bind(this);
        this._fitDesksToScreen = this._fitDesksToScreen.bind(this);
        this._handleKeyUp = this._handleKeyUp.bind(this);
        this._handleKeyDown = this._handleKeyDown.bind(this);
        this._setZoom = this._setZoom.bind(this);
        this._svgHandleDoubleClick = this._svgHandleDoubleClick.bind(this);
        this._svgHandleMouseDown = this._svgHandleMouseDown.bind(this);
        this._svgHandleMouseMove = this._svgHandleMouseMove.bind(this);
        this._svgHandleMouseUp = this._svgHandleMouseUp.bind(this);
        this._svgHandleMouseOut = this._svgHandleMouseOut.bind(this);
        this._svgHandleWheel = this._svgHandleWheel.bind(this);
        this._toggleStateBool = this._toggleStateBool.bind(this);
        this._toolSelection = this._toolSelection.bind(this);
        this.undo = this.undo.bind(this);
        this.redo = this.redo.bind(this);

        this.state = {
            saving: false,
            drawing: this._initialDrawingState,
            drawInternalRooms: false,
            availability: false,
            defaultDeskRotation: 0,
            redoStates: [],
            selectedTool: "Pointer",
            showGrid: true,
            snapToGrid: true,
            undoStates: [],
            zoom: 0.5,
            showBookingsPanel: false
        };
    }

    componentDidMount() {
        document.addEventListener("keyup", this._handleKeyUp);
        document.addEventListener("keydown", this._handleKeyDown);
        this._loadLayout();
    }
    
    componentWillUnmount() {
        document.removeEventListener("keyup", this._handleKeyUp);
        document.removeEventListener("keydown", this._handleKeyDown);
    }

    /** Override of React.Component setState, keeping track of previous state - allows for undo / redo functionality */
    public setState(state: IRoomEditorState, callback?: () => void) {
        const { floorplanId, layoutId } = this.props;
        const undoStates = [...this.state.undoStates];
        undoStates.push({...this.state.drawing});
        const newState = {
            ...state, 
            drawing: {...state.drawing, lastUpdate: new Date().toISOString()}, 
            undoStates, redoStates: []
        };
        localStorage.setItem(`draft_${floorplanId}_${layoutId}`, JSON.stringify(newState.drawing));
        super.setState(newState, callback);
    }

    public render() {
        const { saving, defaultDeskRotation, drawing, selectedShapeIdx, selectedDeskIdx, selectedDoorIdx, selectedTextIdx, selectedFloorIdx, selectedRoomIdx, editingDeskIdx, editingTextIdx, selectedTool, showGrid, snapToGrid, zoom, undoStates, redoStates, deletingDeskId, showBookingsPanel } = this.state;
        const { desks, doors, text, floorplanRectangles, roomRectangles, floorplanShapes, tempShape } = drawing;
        const viewBox = `0 0 ${20000 * zoom} ${20000 * zoom}`;

        const editableSelectionMade = 
            selectedDeskIdx !== undefined || 
            selectedDoorIdx !== undefined || 
            selectedTextIdx !== undefined;
        const removableSelectionMade =
            editableSelectionMade ||
            selectedRoomIdx !== undefined ||
            selectedFloorIdx !== undefined ||
            selectedShapeIdx !== undefined;

        const isSafari = /(Macintosh|iPhone|iPad|iPod).*Safari.*/i.test(navigator.userAgent);

        const svgCss = `
            @keyframes blink {
                100%,
                0% {
                    fill: #500;
                }
                60% {
                    fill: #f00;
                }
            }
            #svgFloorplanEditor.pointerTool #bgColor,
            #svgFloorplanEditor.pointerTool #gridlines {
                cursor: grab;
            }
            #svgFloorplanEditor.pointerTool.grabbed #bgColor,
            #svgFloorplanEditor.pointerTool.grabbed #gridlines {
                cursor: grabbing;
            }
            #svgFloorplanEditor.pointerTool .desk {
                cursor: url('data:image/svg+xml,<svg id="blackPointer" width="22" height="20" version="1.1" baseProfile="full" viewBox="0 0 22 20" xmlns="http://www.w3.org/2000/svg"><g transform="matrix(1.3101052,0,0,1.3101052,-6.2175751,-9.0274217)"><rect x="12.5" y="13.6" transform="matrix(0.9221,-0.3871,0.3871,0.9221,-5.7605,6.5909)" width="2" height="8" /><polygon points="12.2,15.6 12.6,15.5 17.4,15.5 9.2,7.3 9.2,18.5" /></g></svg>') 8 0, auto;
            }
            #svgFloorplanEditor.pointerTool .text.draggable text, 
            #svgFloorplanEditor.textTool .text.draggable text {
                animation: blink 0.8s infinite;
            }
            #svgFloorplanEditor.pointerTool [class*="draggableFloor"] rect,
            #svgFloorplanEditor.pointerTool [class*="draggableRoom"] rect,
            #svgFloorplanEditor.pointerTool [class*="draggableShape"] path{
                stroke: red !important;
            }
            #svgFloorplanEditor.pointerTool .draggable {
                cursor: move;
            }
            #svgFloorplanEditor.pointerTool [id$="RotationHandle"] {
                cursor: url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2232%22%20height%3D%2232%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%20version%3D%221.0%22%20x%3D%220px%22%20y%3D%220px%22%20viewBox%3D%220%200%2024%2024%22%20enable-background%3D%22new%200%200%2024%2024%22%20xml%3Aspace%3D%22preserve%22%3E%3Cpolygon%20points%3D%223%2C3%202%2C10.3%208.9%2C8%20%22%2F%3E%3Cpath%20fill%3D%22none%22%20stroke%3D%22%23000000%22%20stroke-width%3D%222%22%20stroke-linejoin%3D%22round%22%20stroke-miterlimit%3D%2210%22%20d%3D%22M5.8%2C5.4%20%20C7.4%2C3.9%2C9.6%2C3%2C12%2C3c5%2C0%2C9%2C4%2C9%2C9%22%2F%3E%3Cpath%20fill%3D%22none%22%20stroke%3D%22%23000000%22%20stroke-width%3D%222%22%20stroke-linejoin%3D%22round%22%20stroke-miterlimit%3D%2210%22%20stroke-dasharray%3D%223%2C2%22%20d%3D%22%20%20M21%2C12c0%2C5-4%2C9-9%2C9c-3.2%2C0-5.9-1.6-7.5-4.1%22%2F%3E%3Cpath%20fill%3D%22none%22%20stroke%3D%22%23000000%22%20stroke-width%3D%222%22%20stroke-linejoin%3D%22round%22%20stroke-miterlimit%3D%2210%22%20d%3D%22M4.2%2C7.6%20%20C5.7%2C4.8%2C8.6%2C3%2C12%2C3c5%2C0%2C9%2C4%2C9%2C9%22%2F%3E%3C%2Fsvg%3E') -4 -4, pointer;
            }
            #svgFloorplanEditor.deskTool .desk {
                cursor: not-allowed;
            }
            #svgFloorplanEditor.roomTool, #svgFloorplanEditor.floorTool {
                cursor: crosshair;
            }
            
            .notAvailableIcon {
                display: none;
            }
            .nonAvailableDesk .notAvailableIcon, .nonAvailableDesk .availability {
                display: none;
            }
            #floorplan {
                filter: url(#addborder);
            }
            #tempShape {
                stroke: black;
            }
            #shapes, #tempShape {
                fill: white;
            }
            .safari #floorplan {
                filter: none;
            }
            .safari #floorplan rect {
                stroke: none;
            }
        `;

        return (
            <Fabric>
                <Stack style={{position: "relative", height: "100%", overflow: "hidden"}}>
                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center" style={{flex: "none"}}>
                        <RoomEditorTools 
                            desks={desks} 
                            editableSelection={editableSelectionMade}
                            removableSelection={removableSelectionMade}
                            selectedTool={selectedTool}
                            onFitToScreen={this._fitDesksToScreen}
                            onRedo={this.redo}
                            onSettingToggle={this._toggleStateBool}
                            onToolSelection={this._toolSelection}
                            onUndo={this.undo}
                            onEdit={() => {
                                if (selectedDeskIdx !== undefined) {
                                    this.setState({...this.state, editingDeskIdx: selectedDeskIdx});
                                }
                                if (selectedDoorIdx !== undefined) alert('No properties to edit');
                                if (selectedTextIdx !== undefined) {
                                    this.setState({...this.state, editingTextIdx: selectedTextIdx});
                                }
                            }}
                            onDelete={() => {
                                const drawingState = cloneDeep(this.state.drawing);
                                let deletingDeskId: number | undefined;
                                if (selectedDeskIdx !== undefined) {
                                    deletingDeskId = drawingState.desks[selectedDeskIdx].id;
                                    // this.setState({...this.state, deletingDeskId: selectedDeskIdx});
                                    // drawingState.desks = [...this.state.drawing.desks].filter((d, idx) => idx !== selectedDeskIdx);
                                }
                                if (selectedDoorIdx !== undefined) {
                                    drawingState.doors = [...this.state.drawing.doors].filter((d, idx) => idx !== selectedDoorIdx);
                                }
                                if (selectedTextIdx !== undefined) {
                                    drawingState.text = [...this.state.drawing.text].filter((d, idx) => idx !== selectedTextIdx);
                                }
                                if (selectedRoomIdx !== undefined) {
                                    drawingState.roomRectangles = [...this.state.drawing.roomRectangles].filter((d, idx) => idx !== selectedRoomIdx);
                                }
                                if (selectedFloorIdx !== undefined) {
                                    drawingState.floorplanRectangles = [...this.state.drawing.floorplanRectangles].filter((d, idx) => idx !== selectedFloorIdx);
                                }
                                if (selectedShapeIdx !== undefined) {
                                    drawingState.floorplanShapes = [...this.state.drawing.floorplanShapes].filter((s, idx) => idx !== selectedShapeIdx);
                                }
                                
                                this.setState({
                                    ...this.state,
                                    drawing: drawingState,
                                    deletingDeskId,
                                    selectedDeskIdx: undefined,
                                    selectedDoorIdx: undefined,
                                    selectedTextIdx: undefined,
                                    editingTextIdx: undefined,
                                });
                            }}
                            redoStates={redoStates}
                            undoStates={undoStates}
                        />
                        <Stack horizontal horizontalAlign="center" style={{padding: 10}}>
                            { saving === true && (
                                <Spinner style={{marginRight: 10}} />
                            )}
                            { saving === false && (
                                <IconButton disabled={saving} iconProps={{iconName: "Save"}} onClick={() => this._autoSave()} style={{marginRight: 10}} />
                            )}
                            <PrimaryButton style={{marginRight: 20}} text="Publish" onClick={() => {
                                if (window.confirm("Publish this layout and make the desks live to book?")) {
                                    this._publish();
                                }
                            }} />
                            <IconButton iconProps={{iconName: "HomeSolid"}} onClick={() => {
                                this.props.history.push("/");
                            }} />
                        </Stack>
                    </Stack>
                    <div id="drawing" style={{display: "grid", width: "100vw", maxHeight: "calc(100vh - 32px)", gridTemplateColumns: "auto auto", gridTemplateRows: "100%"}}>
                        <div style={{maxWidth: "100vw", overflow: "scroll", gridColumnStart: 1, gridColumnEnd: 2, gridRowStart: 1, gridRowEnd: 2}}>

                            <svg ref={(ref) => this.svg = ref} 
                                id="svgFloorplanEditor"
                                className={"rotate" + defaultDeskRotation + " pointerTool" + (isSafari ? " safari" : "")}
                                version="1.1"
                                baseProfile="full"
                                width="20000" height="20000"
                                xmlns="http://www.w3.org/2000/svg"
                                onWheel={this._svgHandleWheel}
                                viewBox={viewBox}
                                onDoubleClick={this._svgHandleDoubleClick}
                                onMouseDown={this._svgHandleMouseDown}
                                onMouseMove={this._svgHandleMouseMove}
                                onMouseOut={this._svgHandleMouseOut}
                                onMouseUp={this._svgHandleMouseUp}
                                >
                                    <style>{ svgCss }</style>
                                        
                                    <defs id="commonDefs">

                                        { /* Mask for red x on non-available desks */ }
                                        <clipPath id="nonAvailability">
                                            <ellipse ry="7" rx="7" cy="10" cx="10" style={{stroke: "none"}} />
                                        </clipPath>

                                        { /* Thick floorplan outline */ }
                                        <filter id="addborder">
                                            <feMorphology in="SourceAlpha" operator="dilate" radius="4" result="thick" />
                                            <feFlood floodColor="#999" floodOpacity="1" result="color" />
                                            <feComposite in="color" in2="thick" operator="in" result="ouline"/>
                                            <feMerge>
                                                <feMergeNode in="outline" />
                                                <feMergeNode in="SourceGraphic" />
                                            </feMerge>
                                        </filter>

                                        { /* Door template */}
                                        <g id="doorTemplate">
                                            <path d="m1,18l10,-11c0,0.08 6,1.5 6.8,11z" fill="#fff" stroke="#e3e3e3" strokeDasharray="29" strokeLinecap="round" strokeLinejoin="round" />
                                            <line x1="1" y1="18" x2="11" y2="7" stroke="#000" />
                                        </g>

                                        { /* Background Grid Template */ }
                                        <pattern id="smallGrid" width="10" height="10" patternUnits="userSpaceOnUse">
                                            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="gray" strokeWidth="0.5"/>
                                        </pattern>
                                        <pattern id="grid" width="100" height="100" patternUnits="userSpaceOnUse" x="-0.5" y="2">
                                            <rect width="100" height="100" fill="url(#smallGrid)"/>
                                            <path d="M 100 0 L 0 0 0 100" fill="none" stroke="gray" strokeWidth="1"/>
                                        </pattern>

                                    </defs>

                                { /* Background */ }
                                <rect id="bgColor" width="100%" height="100%" fill="#e3e3e3" />
                                <rect id="gridlines" width="100%" height="100%" fill="url(#grid)" style={{visibility: showGrid ? undefined : "hidden" }}/>

                                { /* Floorplan layout */ }
                                <FloorRects floorplanRectangles={floorplanRectangles} selectedFloorIdx={selectedFloorIdx} selectedTool={selectedTool}>
                                    <SvgShapes committedShapes={floorplanShapes} selectedShapeIdx={selectedShapeIdx} selectedTool={selectedTool} />
                                </FloorRects>

                                <PreviewShape tempShape={tempShape} />
                                
                                <RoomRects roomRectangles={roomRectangles} selectedRoomIdx={selectedRoomIdx} selectedTool={selectedTool} />


                                { /* Doorways */ }
                                <Doors doors={doors} selectedDoorIdx={selectedDoorIdx} selectedTool={selectedTool} />

                                { /* Text Labels */ }
                                <TextElements textBoxes={text} selectedTextIdx={selectedTextIdx} selectedTool={selectedTool} />

                                { /* Desks */ }
                                <Desks desks={desks} selectedDeskIdx={selectedDeskIdx} selectedTool={selectedTool} />

                                <DeskCanvasControls desks={desks} selectedDeskIdx={selectedDeskIdx} />
                                <DoorCanvasControls doors={doors} selectedDoorIdx={selectedDoorIdx} />
                                <RectCanvasControls rects={floorplanRectangles} selectedFloorIdx={selectedFloorIdx} type="Floor" />
                                <RectCanvasControls rects={roomRectangles} selectedFloorIdx={selectedRoomIdx} type="Room" />
                                <ShapeCanvasControls shapes={floorplanShapes} selectedShapeIdx={selectedShapeIdx} />
                            </svg>
                        </div>
                        { editingDeskIdx !== undefined && (
                            <DeskEditorPanel 
                                onChange={(desk) => {
                                    const amendedDesks = cloneDeep(desks);
                                    amendedDesks[editingDeskIdx] = desk;
                                    this.setState({
                                        ...this.state,
                                        drawing: {...drawing, desks: amendedDesks}
                                    });
                                }}
                                onDismiss={() => {
                                    super.setState({
                                        ...this.state,
                                        selectedDeskIdx: undefined,
                                        editingDeskIdx: undefined,
                                    });
                                }}
                                snapping={snapToGrid}
                                value={desks[editingDeskIdx]}
                            />
                        )}
                        { editingTextIdx !== undefined && (
                            <TextEditorPanel 
                                key={`editTb_${editingTextIdx}`}
                                onChange={(e, newValue) => {
                                const textBoxes = cloneDeep(text);
                                textBoxes[editingTextIdx] = newValue;
                                super.setState({
                                    ...this.state,
                                    drawing: {...drawing, text: textBoxes}
                                });
                            }} onDismiss={() => {
                                const newState = {...this.state};
                                newState.selectedTextIdx = undefined;
                                newState.editingTextIdx = undefined;
                                super.setState(newState);
                            }} value={text[editingTextIdx]} />
                        )}
                        { !showBookingsPanel && deletingDeskId !== undefined && (
                            <DeskCheckDialog 
                                deskId={deletingDeskId}
                                loadBookings={(deskId) => {
                                    this.setState({...this.state, showBookingsPanel: true});
                                }}
                                onDismiss={(canDelete) => {
                                    if (canDelete) {
                                        const drawingState = cloneDeep(this.state.drawing);
                                        drawingState.desks = [...this.state.drawing.desks].filter((d, idx) => d.id !== deletingDeskId);
                                        this.setState({...this.state, deletingDeskId: undefined, drawing: drawingState});
                                    } else {
                                        this.setState({...this.state, deletingDeskId: undefined});
                                    }
                                }}
                            />
                        )}
                        { showBookingsPanel && (
                            <CancelBookingsPanel 
                                deskId={deletingDeskId}
                                onDismiss={(deskId) => {
                                    this.setState({...this.state, showBookingsPanel: false, deletingDeskId: deskId});
                                }}
                            />
                        )}
                    </div>
                    <ZoomControls currentZoom={zoom} onZoom={this._setZoom} min={0.1} max={0.7} />
                </Stack>
            </Fabric>
        );
    }

    private async _loadLayout() {
        const { floorplanId } = this.props;
        const loggedIn = await isAuthenticated();
        if (!loggedIn) this.props.history.push('/');
        const response = await loadLatestFloorplanEdit(floorplanId);
        if (response.ok) {
            const layoutResponse = await response.json() as IFloorplanLayout;
            const drawing = JSON.parse(layoutResponse.Json) as IRoomEditorDrawingState;
            if (drawing) {
                super.setState({
                    ...this.state,
                    drawing: {
                        ...drawing,
                        floorplanShapes: drawing.floorplanShapes || [],
                        tempShape: []
                    }
                }, this._checkLocalDrafts);
            } else {
                this._checkLocalDrafts();
            }
            this._autoSaveTimer = window.setInterval(this._autoSave, 60000);
        } else {
            alert('Unable to load layout');
        }
    }

    private async _publish() {
        const { floorplanId, layoutId, history } = this.props;
        const { drawing } = this.state;
        super.setState({
            ...this.state,
            saving: true,
        }, async () => {
            const saveResponse = await updateFloorplanLayout(floorplanId, layoutId, drawing);
            if (saveResponse.ok) {
                const response = await publishFloorplanLayout(floorplanId, layoutId, this.state.drawing);
                if (response.ok) {
                    localStorage.clear();
                    history.push('/');
                } else {
                    alert('Unable to publish. Check the console for more details');
                }
            }
        });
    }

    private _autoSave(manualSave?: boolean) {
        const { floorplanId, layoutId } = this.props;
        const { drawing, lastSave } = this.state;
        if (drawing && drawing.lastUpdate) {
            const lastUpdateDt = new Date(drawing.lastUpdate);
            if (manualSave || (!lastSave || lastUpdateDt > lastSave)) {
                super.setState({
                    ...this.state,
                    saving: true,
                }, async () => {
                    const response = await updateFloorplanLayout(floorplanId, layoutId, drawing);
                    if (response.ok) {
                        super.setState({
                            ...this.state,
                            saving: false,
                            lastSave: new Date()
                        });
                    }
                });
            }
        }
    }

    private _checkLocalDrafts() {
        const { drawing: loadedDrawing } = this.state;
        const { floorplanId, layoutId } = this.props;
        const draft = localStorage.getItem(`draft_${floorplanId}_${layoutId}`);
        if (draft) {
            let drawing: IRoomEditorDrawingState | undefined = undefined;
            try {
                drawing = JSON.parse(draft) as IRoomEditorDrawingState;
            } catch (e) {}
            if (drawing) {
                const lastUpdate = drawing.lastUpdate;
                if (lastUpdate) {
                    const draftUpdate = new Date(lastUpdate);
                    let savedUpdate;
                    if (loadedDrawing && loadedDrawing.lastUpdate) savedUpdate = new Date(loadedDrawing.lastUpdate);
                    if (!savedUpdate || draftUpdate > savedUpdate) {
                        let hasContent = false;
                        for (let key in drawing) {
                            if ((drawing as any)[key] && ((drawing as any)[key] as any[]).length) {
                                hasContent = true;
                            }
                        }
                        if (hasContent && window.confirm('A draft was found locally that is newer than what is saved to the server. Restore?')) {
                            super.setState({
                                ...this.state,
                                drawing
                            });
                        }
                    }
                }
            }
        }
    }

    private _svgHandleDoubleClick(e: React.MouseEvent<SVGSVGElement>) {
        if (this.svg) {
            const { x, y } = this._getSvgMousePosition(e, this.svg);
            if (x !== undefined && y !== undefined) {
                const { clickedDeskIdx } = this._checkSelection(x, y, e.clientX, e.clientY);
                if (clickedDeskIdx !== undefined) {
                    super.setState({
                        ...this.state,
                        editingDeskIdx: clickedDeskIdx
                    });
                    return;
                } 
            }
            const element = document.elementFromPoint(e.clientX, e.clientY);
            if (element && (element.nodeName === "text" || element.nodeName === "tspan")) {
                let parentElement: Node | null = element as SVGElement;
                while (parentElement !== null && parentElement.nodeName !== "svg") {
                    parentElement = parentElement.parentNode;
                }
                if (parentElement) {
                    const idxStr = (parentElement as SVGSVGElement).getAttribute("data-idx");
                    if (idxStr) {
                        const idx = parseInt(idxStr, 10);
                        super.setState({
                            ...this.state,
                            selectedTextIdx: idx,
                            editingTextIdx: idx,
                        });
                    }
                }
            }
        }
    }

    private _svgHandleMouseDown(e: React.MouseEvent<SVGSVGElement>) {
        const { selectedDeskIdx, selectedDoorIdx, selectedTextIdx, selectedTool, selectedFloorIdx, selectedRoomIdx, selectedShapeIdx, snapToGrid } = this.state;
        const element = document.elementFromPoint(e.clientX, e.clientY);
        if (this.svg && element) {
            if (/RotationHandle/i.test(element.id)) {
                let type = element.id.replace("RotationHandle", "");
                type = type.charAt(0).toUpperCase() + type.slice(1);
                const selectedObjIdx = (this.state as any)[`selected${type}Idx`];
                this._startRotation(e, type as StampObj, selectedObjIdx);
                return;
            }
        }
        if (this.svg) {
            const { x, y } = this._getSvgMousePosition(e, this.svg);
            if (x !== undefined && y !== undefined) {
                if (selectedTool === "Pointer") {
                    const { clickedDeskIdx, clickedDoorIdx, clickedTextIdx, clickedFloorIdx, clickedRoomIdx, clickedShapeIdx } = this._checkSelection(x, y, e.clientX, e.clientY);
                    if (clickedDeskIdx !== undefined) {
                        this._startMoveOrSelect("MoveDesk", selectedDeskIdx, clickedDeskIdx, x, y);
                    } else if (clickedDoorIdx !== undefined) {
                        this._startMoveOrSelect("MoveDoor", selectedDoorIdx, clickedDoorIdx, x, y);
                    } else if (clickedTextIdx !== undefined) {
                        this._startMoveOrSelect("MoveText", selectedTextIdx, clickedTextIdx, x, y);
                    } else if (clickedRoomIdx !== undefined) {
                        this._startMoveOrSelect("MoveRoom", selectedRoomIdx, clickedRoomIdx, x, y);
                    } else if (clickedFloorIdx !== undefined) {
                        this._startMoveOrSelect("MoveFloor", selectedFloorIdx, clickedFloorIdx, x, y);
                    } else if (clickedShapeIdx !== undefined) {
                        this._startMoveOrSelect("MoveShape", selectedShapeIdx, clickedShapeIdx, x, y);
                    } else {
                        this._startGrabCanvas(e);
                    }
                }
                if (selectedTool === "Floor" || selectedTool === "Room" || selectedTool === "Pen") {
                    // prevent starting a room / office drawing directly over a desk
                    const { clickedDeskIdx } = this._checkSelection(x, y, e.clientX, e.clientY);
                    if (clickedDeskIdx === undefined) {
                        this._doRoomDrawing(e, x, y, snapToGrid);
                    }
                }
            }
        }
    }

    private _svgHandleMouseMove(e: React.MouseEvent<SVGSVGElement>) {
        const { availability, defaultDeskRotation, drawing, selectedTool, snapToGrid } = this.state;
        const { desks, doors } = drawing;
        if (this.svg) {
            
            // update mouse coords
            this._liveX = e.clientX;
            this._liveY = e.clientY;
            let { x, y } = this._getSvgMousePosition(e, this.svg);
            
            if (x !== undefined && y !== undefined) {
                
                // desk coordinates are offset
                let deskX = x - 10;
                let deskY = y - 10;

                if (snapToGrid) {
                    if (x % 10 !== 0) x = Math.round(x / 10) * 10;
                    if (y % 10 !== 0) y = Math.round(y / 10) * 10;
                    if (deskX % 10 !== 0) deskX = Math.round(deskX / 10) * 10;
                    if (deskY % 10 !== 0) deskY = Math.round(deskY / 10) * 10;
                }

                // update coordinates in view (for debug purposes)
                const coordsContainer = document.querySelector("#coords");
                if (coordsContainer) {
                    coordsContainer.innerHTML = `(${deskX}, ${deskY})`;
                }

                // if an operation has been set by mousedown, act on it during mousemove
                if (this._penInUse) {
                    this._previewNextPenNode(e, x, y, snapToGrid, this._mouseDownOperation);
                } else {
                    if (this._mouseDownOperation) {
                        const { initialCoords, itemIdx, type } = this._mouseDownOperation;
                        if (this._mouseDownOperation.type === "Scroll") {
                            this._performCanvasScroll(e, initialCoords);
                        }
                        if (this._mouseDownOperation.type === "DrawFloorplan" ||
                            this._mouseDownOperation.type === "DrawRoom") {
                            this._drawRoomLayoutPreviewBox(type, initialCoords, x, y, snapToGrid);
                        }
                        if (this._mouseDownOperation.type === "RotateDesk") {
                            this._performDeskRotation(desks, itemIdx, x, y, snapToGrid);
                        }
                        if (this._mouseDownOperation.type === "RotateDoor") {
                            this._performDoorRotation(doors, itemIdx, x, y, snapToGrid);
                        }
                        if (this._mouseDownOperation.type === "RotateFloor") {
                            this._performFloorRotation(itemIdx, x, y, snapToGrid);
                        }
                        if (this._mouseDownOperation.type === "RotateRoom") {
                            this._performRoomRotation(itemIdx, x, y, snapToGrid);
                        }
                        if (this._mouseDownOperation.type === "RotateShape") {
                            this._performShapeRotation(itemIdx, x, y, snapToGrid);
                        }
                        if (this._mouseDownOperation.type === "MoveDesk") {
                            this._moveDraggable("Desk", itemIdx, deskX, deskY);
                        }
                        if (this._mouseDownOperation.type === "MoveDoor") {
                            this._moveDraggable("Door", itemIdx, deskX, deskY);
                        }
                        if (this._mouseDownOperation.type === "MoveText") {
                            this._moveDraggable("Text", itemIdx, deskX + 10, deskY + 10);
                        }
                        if (this._mouseDownOperation.type === "MoveFloor") {
                            this._moveDraggable("Floor", itemIdx, x, y, initialCoords);
                        }
                        if (this._mouseDownOperation.type === "MoveRoom") {
                            this._moveDraggable("Room", itemIdx, x, y, initialCoords);
                        }
                        if (this._mouseDownOperation.type === "MoveShape") {
                            this._moveDraggable("Shape", itemIdx, x, y, initialCoords);
                        }
                    } else {
                        // Otherwise, hover behaviour needs to be scripted for the desk tool, as we simulate rotation etc.
                        const collision = this._checkDeskCollision(deskX, deskY);
                        if (selectedTool === "Desk" || selectedTool === "Door") {
                            this._handleStampableToolHover(deskX, deskY, defaultDeskRotation, collision, availability);
                        }
                    }
                }
                
            }
        }
    }

    private _previewNextPenNode(e: React.MouseEvent<SVGSVGElement, MouseEvent>, mouseX: number, mouseY: number, snapToGrid: boolean, _mouseDownOperation: IMouseDownOperation | null) {
        const { drawing } = this.state;
        let { tempShape } = drawing;

        if (snapToGrid) {
            if (mouseX % 10 !== 0) mouseX = Math.round(mouseX / 10) * 10;
            if (mouseY % 10 !== 0) mouseY = (Math.round(mouseY / 10) * 10) + 2.5;
        }

        /* If tempShape.length > 1, we have a shape in progress and need to remove any prior preview nodes */
        tempShape = tempShape.filter(s => !s.preview);

        /* If we have a mouseDownOperation, the pen is "down" and possibly dragging */
        let initX: number | undefined;
        let initY: number | undefined;
        if (this._mouseDownOperation) {
            [initX, initY] = this._mouseDownOperation.initialCoords;
        }
        const isDrag = 
            /* Drag / curve not allowed for first point */
            tempShape.length > 1 &&
            /* Drag / curve allowed if mouse moved more than 5 pixels */
            this._mouseDownOperation && 
            (Math.abs(mouseX - initX!) > 5 || Math.abs(mouseY - initY!) > 5);

        /* Add the preview node to the tempShape array */
        if (tempShape && tempShape.length) {
            /* Depending on whether the user dragged or simply clicked a point, we need to add a new point or a new curve */
            const lastShapeEl = tempShape[tempShape.length - 1];
            const { type: lastShapeElType } = lastShapeEl;
            switch (lastShapeElType) {
                case "Move":
                case "Line": 
                case "ReflectedCurve": {
                    if (isDrag) {
                        const controlX = initX! + ((mouseX - initX!) * -1);
                        const controlY = initY! + ((mouseY - initY!) * -1);
                        tempShape.push({
                            type: "Curve",
                            x: initX!, y: initY!,
                            x1: controlX, y1: controlY,
                            preview: true
                        });
                    } else {
                        tempShape.push({
                            type: "Line",
                            x: mouseX, y: mouseY,
                            preview: true
                        });
                    }
                    break;
                }
                case "Curve": {
                    if (isDrag) {
                        const controlX = initX! + ((mouseX - initX!) * -1);
                        const controlY = initY! + ((mouseY - initY!) * -1);
                        tempShape.push({
                            type: "Curve",
                            x: initX!, y: initY!,
                            x1: controlX, y1: controlY,
                            preview: true
                        });
                    } else {
                        tempShape.push({
                            type: "ReflectedCurve",
                            x: mouseX, y: mouseY,
                            preview: true
                        });
                    }
                    break;
                }
            }
            super.setState({
                ...this.state,
                drawing: {
                    ...this.state.drawing,
                    tempShape
                }
            });   
        }
    }

    /** SVG Mouse Up Handler. Based on previously set MouseDown operations and movements, evaluates which data should be changed in state
     * based on user actions. State changes trigger a re-render of the SVG canvas.
     */
    private _svgHandleMouseUp(e: React.MouseEvent<SVGSVGElement>) {
        const { availability, defaultDeskRotation, drawing, selectedTool, snapToGrid } = cloneDeep(this.state);
        const { desks, doors, text } = drawing;
        if (this.svg) {
            let { x: mouseX, y: mouseY } = this._getSvgMousePosition(e, this.svg);
            if (this._mouseDownOperation) {
                const { itemIdx, type } = this._mouseDownOperation;
                if (["MoveDesk", "MoveDoor", "MoveText", "MoveFloor", "MoveRoom", "MoveShape"].indexOf(type) >= 0) {
                    this._commitMovedObject(type, itemIdx);
                }
                if (/Rotate/i.test(type)) {
                    const rotationType = type.replace("Rotate", "");
                    if (mouseX !== undefined && mouseY !== undefined) {
                        this._commitRotation(rotationType as StampObj, itemIdx, mouseX, mouseY, snapToGrid);
                    }
                }
                // if (type === "RotateDesk") {
                //     if (mouseX !== undefined && mouseY !== undefined) {
                //         this._commitDeskRotation(desks, itemIdx, mouseX, mouseY, snapToGrid);
                //     }
                // }
                // if (type === "RotateDoor") {
                //     if (mouseX !== undefined && mouseY !== undefined) {
                //         this._commitDoorRotation(doors, itemIdx, mouseX, mouseY, snapToGrid);
                //     }
                // }
                if (type === "Scroll") {
                    this._releaseGrabbedCanvas(this.svg);
                }
                if (type === "DrawWithPen") {
                    this._commitPenNode(e, mouseX, mouseY, snapToGrid);
                }
            } else {
                let drawingStateChanged: boolean = false;
                if (mouseX !== undefined && mouseY !== undefined) {
                    if (selectedTool === "Desk") {
                        this._addNewStampable(mouseX, mouseY, snapToGrid, desks, defaultDeskRotation, -10, { bookable: !availability });
                        drawingStateChanged = true;
                    }
                    if (selectedTool === "Door") {
                        this._addNewStampable(mouseX, mouseY, snapToGrid, doors, defaultDeskRotation, -10);
                        drawingStateChanged = true;
                    }
                    if (selectedTool === "Text") {
                        this._addNewStampable(mouseX, mouseY, snapToGrid, text, defaultDeskRotation, 0, { text: undefined, fontSize: TBFontSize.large });
                        drawingStateChanged = true;
                    }
                }
                // deselect items if the user has clicked the grid or background
                const element = document.elementFromPoint(e.clientX, e.clientY);
                const newState = {
                    ...this.state,
                    drawing,
                };
                if (element && (element.id === "gridlines" || element.id === "bgColor")) {
                    newState.selectedDeskIdx = undefined;
                    newState.selectedDoorIdx = undefined;
                    newState.selectedTextIdx = undefined;
                    newState.selectedFloorIdx = undefined;
                    newState.selectedRoomIdx = undefined;
                    newState.selectedShapeIdx = undefined;
                    newState.editingDeskIdx = undefined;
                    newState.editingTextIdx = undefined;
                }
                if (drawingStateChanged) {
                    if (selectedTool === "Text") {
                        newState.selectedTextIdx = text.length - 1;
                        newState.editingTextIdx = text.length - 1;
                    }
                    this.setState(newState);
                } else {
                    super.setState(newState);
                }
            }
        }
    }

    private _commitPenNode(e: React.MouseEvent<SVGSVGElement, MouseEvent>, mouseX: number | undefined, mouseY: number | undefined, snapToGrid: boolean) {
        const { drawing } = this.state;
        let { tempShape, floorplanShapes } = drawing;
        if (this._mouseDownOperation) {
            const [initX,initY] = this._mouseDownOperation.initialCoords;
            if (mouseX !== undefined && mouseY !== undefined) {
                if (snapToGrid) {
                    if (mouseX % 10 !== 0) mouseX = Math.round(mouseX / 10) * 10;
                    if (mouseY % 10 !== 0) mouseY = (Math.round(mouseY / 10) * 10) + 2.5;
                }
                const isDrag = 
                    /* Drag / curve not allowed for first point */
                    tempShape.length > 2 &&
                    /* Drag / curve allowed if mouse moved more than 5 pixels */
                    (Math.abs(mouseX - initX) > 5 || Math.abs(mouseY - initY) > 5);

                if (tempShape && tempShape.length) {
                    /* If tempShape.length > 1, we have a shape in progress */
                    tempShape = tempShape.filter(s => !s.preview);

                    const { x: shapeStartX, y: shapeStartY } = tempShape[0] as ISvgStart;
                    const isClosing = tempShape.length > 2 && Math.abs(mouseX - shapeStartX) < 5 && Math.abs(mouseY - shapeStartY) < 5;

                    const lastShapeEl = tempShape[tempShape.length - 1];
                    const { type: lastShapeElType } = lastShapeEl;

                    /* Depending on whether the user dragged or simply clicked a point, we need to add a new point or a new curve */
                    switch (lastShapeElType) {
                        case "Move":
                        case "Line": 
                        case "ReflectedCurve": {
                            if (isDrag) {
                                const controlX = initX + ((mouseX - initX) * -1);
                                const controlY = initY + ((mouseY - initY) * -1);
                                tempShape.push({
                                    type: "Curve",
                                    x: initX, y: initY,
                                    x1: controlX, y1: controlY
                                });
                            } else {
                                tempShape.push({
                                    type: "Line",
                                    x: mouseX, y: mouseY
                                });
                            }
                            break;
                        }
                        case "Curve": {
                            if (isDrag) {
                                const controlX = initX + ((mouseX - initX) * -1);
                                const controlY = initY + ((mouseY - initY) * -1);
                                tempShape.push({
                                    type: "Curve",
                                    x: initX, y: initY,
                                    x1: controlX, y1: controlY
                                });
                            } else {
                                tempShape.push({
                                    type: "ReflectedCurve",
                                    x: mouseX, y: mouseY
                                });
                            }
                            break;
                        }
                    }

                    const setState = isClosing ? this.setState.bind(this) : super.setState.bind(this);
                    if (isClosing) {
                        const newShape = [...tempShape];
                        const svgShape = this._calculateShapeObject(newShape);
                        floorplanShapes = [...floorplanShapes, svgShape];
                        this._penInUse = false;
                    }
                    setState({
                        ...this.state,
                        drawing: {
                            ...this.state.drawing,
                            floorplanShapes,
                            tempShape: isClosing ? [] : tempShape
                        }
                    }, () => {
                        this._mouseDownOperation = null;
                    });

                }
            }
        }
    }
    private _calculateShapeObject(newShape: SvgShapeComponent[]): ISvgShape {
        // find the most left value of x or x1 in newShape's elements
        const fallback = Number.MAX_SAFE_INTEGER;
        let [ minX, minY, maxX, maxY ] = newShape.reduce((prev, cur, idx, arr) => {
            let minx = (cur as any).x || fallback;
            let miny = (cur as any).y || fallback;
            let maxx = (cur as any).x || 0;
            let maxy = (cur as any).y || 0;
            if (cur.type === "Curve") {
                const curve = cur as ISvgCurve;
                const [ xVals, yVals ] = this._calculateCurvePoints(curve, arr[idx - 1]);
                const curveMin = [ Math.min(...xVals), Math.min(...yVals) ];
                const curveMax = [ Math.max(...xVals), Math.max(...yVals) ];
                if (curveMin[0] < minx) minx = curveMin[0];
                if (curveMin[1] < miny) miny = curveMin[1];
                if (curveMax[0] > maxx) maxx = curveMax[0];
                if (curveMax[1] > maxy) maxy = curveMax[1];
            }
            return [ Math.min(prev[0], minx), Math.min(prev[1], miny), Math.max(prev[2], maxx), Math.max(prev[3], maxy) ];
        }, [fallback, fallback, 0, 0]);
        newShape.forEach((el) => {
            if ((el as any).x) (el as any).x -= minX;
            if ((el as any).x1) (el as any).x1 -= minX;
            if ((el as any).y) (el as any).y -= minY;
            if ((el as any).y1) (el as any).y1 -= minY;
        });
        return {
            path: newShape,
            rotation: 0,
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            id: this.state.drawing.floorplanShapes.length,
        };
    }
    private _calculateCurvePoints(curve: ISvgCurve, arg1: SvgShapeComponent): [any, any] {
        // Given points: start, control, and end
        const x0 = (arg1 as any).x, y0 = (arg1 as any).y; // Start point
        const x1 = curve.x1, y1 = curve.y1; // Control point
        const x2 = curve.x, y2 = curve.y; // End point

        // Compute tx and ty
        const denom_x = (x0 - 2 * x1 + x2);
        const denom_y = (y0 - 2 * y1 + y2);
        const tx = denom_x !== 0 ? (x0 - x1) / denom_x : null;
        const ty = denom_y !== 0 ? (y0 - y1) / denom_y : null;

        // Parametric functions for x and y
        function bezier_x(t: number): number {
            return (1 - t) ** 2 * x0 + 2 * (1 - t) * t * x1 + t ** 2 * x2;
        }

        function bezier_y(t: number): number {
            return (1 - t) ** 2 * y0 + 2 * (1 - t) * t * y1 + t ** 2 * y2;
        }

        // Evaluate critical points and endpoints
        const x_values: number[] = [bezier_x(0), bezier_x(1)];
        const y_values: number[] = [bezier_y(0), bezier_y(1)];

        if (tx !== null && tx >= 0 && tx <= 1) {
            x_values.push(bezier_x(tx));
        }
        if (ty !== null && ty >= 0 && ty <= 1) {
            y_values.push(bezier_y(ty));
        }

        return [x_values, y_values];
    }

    /** SVG Canvas Mouse Out handler. Ensures cursors and other mouse-over previews are cleared when the cursor leaves the canvas. */
    private _svgHandleMouseOut(e: React.MouseEvent<SVGSVGElement>) {
        // hide svg-stamp / cursor
        if (this.svg) {    
            let cursor = this.svg.querySelector('#cursor');
            if (cursor) {
                cursor.remove();
            }
        }
    }

    private _svgHandleWheel(e: React.WheelEvent<SVGSVGElement>) {
        if (this.svg) {
            const scrollTop = this.svg.scrollTop;
            const scrollLeft = this.svg.scrollLeft;
            this.svg.scrollTo({ top: scrollTop + e.deltaY, left: scrollLeft + e.deltaX });
        }
    }

    /** Pointer Tool Operation: Begins a scrolling operation triggered by the user clicking a blank area of the SVG canvas, while using the pointer tool */
    private _startGrabCanvas(e: React.MouseEvent<SVGSVGElement, MouseEvent>) {
        const drawing = document.querySelector('#drawing');
        if (this.svg && drawing && drawing.firstChild) {
            this.svg.classList.add("grabbed");
            const el = drawing.firstChild as HTMLElement;
            this._mouseDownOperation = {
                initialCoords: [e.clientX, e.clientY, el.scrollLeft, el.scrollTop],
                itemIdx: -1,
                type: "Scroll"
            };
        }
    }

    /** Releases the grabbed canvas by clearing the current mouse down operation. Also clears any selected desks. */
    private _releaseGrabbedCanvas(svg: SVGSVGElement) {
        svg.classList.remove("grabbed");
        this._mouseDownOperation = null;
        super.setState({
            ...this.state,
            selectedDeskIdx: undefined,
            selectedDoorIdx: undefined,
            selectedFloorIdx: undefined,
            selectedRoomIdx: undefined,
            selectedShapeIdx: undefined,
            selectedTextIdx: undefined,
            editingDeskIdx: undefined,
            editingTextIdx: undefined,
        });
    }

    /** Pointer Tool Operation: selects a movable object, or begins a move operation */
    private _startMoveOrSelect(type: MouseDownOperationType, selectedIdx: number | undefined, clickedIdx: number | undefined, x: number, y: number) {
        if (selectedIdx !== undefined && selectedIdx === clickedIdx) {
            this._mouseDownOperation = {
                initialCoords: [x, y],
                itemIdx: selectedIdx,
                type
            };
        }
        if (selectedIdx !== clickedIdx) {
            const newState = {
                ...this.state,
                selectedDeskIdx: undefined,
                selectedDoorIdx: undefined,
                selectedTextIdx: undefined,
                selectedRoomIdx: undefined,
                selectedFloorIdx: undefined,
                selectedShapeIdx: undefined,
                editingTextIdx: undefined,
            };
            (newState as any)[`selected${type.replace("Move","")}Idx`] = clickedIdx;
            super.setState(newState);
        }
    }

    private _startRotation(e: React.MouseEvent<SVGSVGElement, MouseEvent>, type: StampObj, selectedObjIdx: number | undefined) {
        const { x, y } = this._getSvgMousePosition(e, this.svg);
        if (selectedObjIdx !== undefined && x !== undefined && y !== undefined) {
            this._mouseDownOperation = {
                initialCoords: [x, y],
                itemIdx: selectedObjIdx,
                type: ("Rotate" + type) as MouseDownOperationType
            };
        }
    }

    /** Desk Control Handle Operation: begins a desk rotation */
    private _startDeskRotation(e: React.MouseEvent<SVGSVGElement, MouseEvent>, selectedDeskIdx: number | undefined) {
        const { x, y } = this._getSvgMousePosition(e, this.svg);
        if (selectedDeskIdx !== undefined && x !== undefined && y !== undefined) {
            this._mouseDownOperation = {
                initialCoords: [x, y],
                itemIdx: selectedDeskIdx,
                type: "RotateDesk"
            };
        }
    }

    /** Door Control Handle Operation: begins a door rotation */
    private _startDoorRotation(e: React.MouseEvent<SVGSVGElement, MouseEvent>, selectedDoorIdx: number | undefined) {
        const { x, y } = this._getSvgMousePosition(e, this.svg);
        if (selectedDoorIdx !== undefined && x !== undefined && y !== undefined) {
            this._mouseDownOperation = {
                initialCoords: [x, y],
                itemIdx: selectedDoorIdx,
                type: "RotateDoor"
            };
        }
    }

    /** Room Drawing: handles draw operations with the Room, Floor and Pen tools */
    private _doRoomDrawing(e: React.MouseEvent<SVGSVGElement, MouseEvent>, x: number, y: number, snapToGrid: boolean) {
        const { roomRectangles, floorplanRectangles } = this.state.drawing;
        if (snapToGrid) {
            if (x % 10 !== 0) x = Math.round(x / 10) * 10;
            if (y % 10 !== 0) y = (Math.round(y / 10) * 10) + 2.5;
        }
        if (!this._mouseDownOperation) {
            let opType: MouseDownOperationType = "DrawRoom";
            if (this.state.selectedTool === "Floor") opType = "DrawFloorplan";
            if (this.state.selectedTool === "Pen") {
                opType = "DrawWithPen";
                if (!this._penInUse) {
                    this._penInUse = true;
                    this.setState({
                        ...this.state,
                        drawing: {
                            ...this.state.drawing,
                            tempShape: [{
                                type: "Move",
                                x, y
                            }]
                        }
                    });
                }
            }
            this._mouseDownOperation = {
                initialCoords: [x, y],
                itemIdx: -1,
                type: opType
            };
            return;
        }
        /* With Floor and Room operations, the first click commences a room drawing operation, the second commits the drawn room or shape to state */
        if (this._mouseDownOperation &&
            (
                this._mouseDownOperation.type === "DrawFloorplan" ||
                this._mouseDownOperation.type === "DrawRoom"
            )
        ) {
            /* Take the lower and higher values from initial coordinates and the current mouse position, these are our bounding box coords for a room
            container or floorspace */
            let x1 = Math.min(x, this._mouseDownOperation.initialCoords[0]);
            let x2 = Math.max(x, this._mouseDownOperation.initialCoords[0]);
            let y1 = Math.min(y, this._mouseDownOperation.initialCoords[1]);
            let y2 = Math.max(y, this._mouseDownOperation.initialCoords[1]);
            /* Adjust for snap to grid */
            if (snapToGrid) {
                if (x1 % 10 !== 0) x1 = Math.round(x1 / 10) * 10;
                if (x2 % 10 !== 0) x2 = Math.round(x2 / 10) * 10;
                if (y1 % 10 !== 0) y1 = Math.round(y1 / 10) * 10 + 2.5;
                if (y2 % 10 !== 0) y2 = Math.round(y2 / 10) * 10 + 2.5;
            }
            /* Define rectangle object */
            const rect: IRoomRectangle = {
                x: x1, y: y1,
                width: (x2 - x1), height: (y2 - y1),
                rotation: 0
            };
            /* Commit to state */
            let newState = cloneDeep(this.state);
            let rooms: IRoomRectangle[];
            if (this._mouseDownOperation.type === "DrawFloorplan") {
                rooms = [...floorplanRectangles];
                rooms.push(rect);
                newState.drawing.floorplanRectangles = rooms;
            } else {
                rooms = [...roomRectangles];
                rooms.push(rect);
                newState.drawing.roomRectangles = rooms;
            }
            this.setState(newState, () => {
                this._mouseDownOperation = null;
                if (this.svg) {
                    const room = this.svg.querySelector('#roomDrawing');
                    if (room) room.remove();
                }
            });
        }
    }

    /** Injects a desk-preview in to the SVG canvas, giving the user a stamp-like cursor that reflects the set desk rotation etc. */
    private _handleStampableToolHover(deskX: number, deskY: number, defaultDeskRotation: number, collision: boolean, availability: boolean) {
        if (this.svg) {
            let cursorContainer = this.svg.querySelector('#cursor');
    
            if (!cursorContainer) {
                cursorContainer = document.createElementNS(xmlns, 'g');
                cursorContainer.id = "cursor";
                cursorContainer.setAttribute("transform", `translate(${deskX}, ${deskY})`);
                let cursor: SVGGElement | undefined = undefined;
                if (this.state.selectedTool === "Desk") {
                    cursor = createDeskSvg();
                } else if (this.state.selectedTool === "Door") {
                    cursor = createDoorSvg();
                }
                if (cursor) {
                    cursor.setAttribute("style", "pointer-events: none; transform-origin: 10px 12px;");
                    cursorContainer.appendChild(cursor);
                }
                this.svg.appendChild(cursorContainer);
            } else {
                cursorContainer.setAttribute("transform", `translate(${deskX}, ${deskY})`);
                const cursor = cursorContainer.firstChild as SVGGElement;
                if (cursor) {
                    if (cursor.getAttribute("transform") !== `rotate(${defaultDeskRotation})`) {
                        cursor.setAttribute("transform", `rotate(${defaultDeskRotation})`);
                    }
                    if (this.state.selectedTool === "Desk") {
                        const deskRect = cursor.querySelector('rect');
                        if (deskRect) {
                            deskRect.setAttribute("fill", collision ? "#e3bcbc" : "#cccccc");
                        }
                        if (availability) {
                            cursor.classList.add("nonAvailableDesk");
                        } else {
                            cursor.classList.remove("nonAvailableDesk");
                        }
                    } else if (this.state.selectedTool === "Door") {
                        const doorPath = cursor.querySelector('path');
                        if (doorPath) {
                            doorPath.setAttribute("fill", collision ? "#e3bcbc" : "#fff");
                        }
                    }
                }
            }
        }
    }

    /** Moves the draggable item, such as a desk or door */
    private _moveDraggable(type: StampObj, objectIdx: number, x: number, y: number, initialClickCoords?: number[]) {
        const element = document.querySelector(`.draggable${type}-${objectIdx}`);
        const controlHandles = document.querySelector(`#${type.toLowerCase()}ControlHandles`);
        if (element) {
            if (initialClickCoords) {
                const arr = type === "Floor" ? "floorplanRectangles" : (type === "Room" ? "roomRectangles" : "floorplanShapes");
                const shape = (this.state.drawing as any)[arr][objectIdx];
                const curX = shape.x;
                const curY = shape.y;
                const movementX = x - initialClickCoords[0];
                const movementY = y - initialClickCoords[1];
                x = curX + movementX; //+ offsetX;
                y = curY + movementY; // + offsetY;
            }
            element.setAttribute("x", x.toString());
            element.setAttribute("y", y.toString());
        }
        if (controlHandles) {
            if (type === "Desk" || type === "Door") {
                controlHandles.setAttribute("x", (x + 10).toString());
                controlHandles.setAttribute("y", (y + 12.5).toString());
            } else if (type === "Shape" || type === "Floor" || type === "Room") {
                const arrName = type === "Shape" ? "floorplanShapes" : (type === "Floor" ? "floorplanRectangles" : "roomRectangles");
                const shape = (this.state.drawing as any)[arrName][objectIdx];
                controlHandles.setAttribute("x", (shape.x+(shape.width! / 2)).toString());
                controlHandles.setAttribute("y", (shape.y+(shape.height! / 2)).toString());
            }
            
        }
    }

    /** Rotates the selected desk (at index), based on mouse position */
    private _performDeskRotation(desks: IDesk[], itemIdx: number, mouseX: number, mouseY: number, snapToGrid: boolean): number {
        const desk = desks[itemIdx];
        // Find the angle between the clicked mouse position and the desk's center
        mouseX = mouseX - (desk.x + 10);
        mouseY = mouseY - (desk.y + 10);
        const a = Math.atan2(mouseY, mouseX);
        let deg = Math.round(a * (180 / Math.PI));
        // Fix to 45 degree increments if snapToGrid is on
        if (snapToGrid) {
            if (deg % 45 !== 0)
                deg = Math.round(deg / 45) * 45;
        }
        // Amend desk SVG element for live-preview
        deg = Math.round(deg);
        const deskElement = document.querySelector('.draggableDesk-' + itemIdx);
        const controlHandles = document.querySelector('#deskControlHandles');
        if (deskElement && deskElement.firstChild) {
            (deskElement.firstChild as SVGGElement).setAttribute("transform", `rotate(${deg})`);
        }
        if (controlHandles) {
            (controlHandles.firstChild as SVGGElement).setAttribute("transform", `rotate(${deg})`);
        }
        return deg;
    }

    /** Rotates the selected door (at index), based on mouse position */
    private _performDoorRotation(doors: IStampable[], itemIdx: number, mouseX: number, mouseY: number, snapToGrid: boolean) {
        const door = doors[itemIdx];
        // Find the angle between the clicked mouse position and the desk's center
        mouseX = mouseX - (door.x + 10);
        mouseY = mouseY - (door.y + 10);
        const a = Math.atan2(mouseY, mouseX);
        let deg = Math.round(a * (180 / Math.PI));
        // Fix to 45 degree increments if snapToGrid is on
        if (snapToGrid) {
            if (deg % 45 !== 0)
                deg = Math.round(deg / 45) * 45;
        }
        // Amend desk SVG element for live-preview
        deg = Math.round(deg);
        const doorElement = document.querySelector('.draggableDoor-' + itemIdx);
        const controlHandles = document.querySelector('#doorControlHandles');
        if (doorElement && doorElement.firstChild) {
            (doorElement.firstChild as SVGGElement).setAttribute("transform", `rotate(${deg}, 10, 12)`);
        }
        if (controlHandles) {
            (controlHandles.firstChild as SVGGElement).setAttribute("transform", `rotate(${deg})`);
        }
        return deg;
    }

    private _performFloorRotation(itemIdx: number, mouseX: number, mouseY: number, snapToGrid: boolean): number {
        return this._performFloorOrRoomRectRotation("Floor", this.state.drawing.floorplanRectangles, itemIdx, mouseX, mouseY, snapToGrid);
    }

    private _performRoomRotation(itemIdx: number, mouseX: number, mouseY: number, snapToGrid: boolean): number {
        return this._performFloorOrRoomRectRotation("Room", this.state.drawing.roomRectangles, itemIdx, mouseX, mouseY, snapToGrid);
    }

    private _performFloorOrRoomRectRotation(type: "Room" | "Floor", rects: IRoomRectangle[], itemIdx: number, mouseX: number, mouseY: number, snapToGrid: boolean): number {
        const rect = rects[itemIdx];
        // Find the angle between the clicked mouse position and the floor object's center
        mouseX = mouseX - (rect.x + (rect.width! / 2));
        mouseY = mouseY - (rect.y + (rect.height! / 2));
        const a = Math.atan2(mouseY, mouseX);
        let deg = Math.round(a * (180 / Math.PI));
        // Fix to 45 degree increments if snapToGrid is on
        if (snapToGrid) {
            if (deg % 45 !== 0)
                deg = Math.round(deg / 45) * 45;
        }
        // Amend floor SVG element for live-preview
        deg = Math.round(deg);
        const floorElement = document.querySelector(`.draggable${type}-${itemIdx}`) as SVGRectElement;
        const controlHandles = document.querySelector(`#${type.toLowerCase()}ControlHandles`);
        if (floorElement && floorElement.firstChild) {
            const gEl = floorElement.firstChild as SVGGElement;
            // transform origin should be the center of the object
            const transformOriginX = rect.width! / 2;
            const transformOriginY = rect.height! / 2;
            gEl.setAttribute("transform", `rotate(${deg}, ${transformOriginX}, ${transformOriginY})`);
        }
        if (controlHandles) {
            (controlHandles.firstChild as SVGGElement).setAttribute("transform", `rotate(${deg})`);
        }
        return deg;
    }

    private _performShapeRotation(itemIdx: number, mouseX: number, mouseY: number, snapToGrid: boolean): number {
        const shapes = this.state.drawing.floorplanShapes;
        const shape = shapes[itemIdx];
        // Find the angle between the clicked mouse position and the shape object's center
        mouseX = mouseX - (shape.x + (shape.width! / 2));
        mouseY = mouseY - (shape.y + (shape.height! / 2));
        const a = Math.atan2(mouseY, mouseX);
        let deg = Math.round(a * (180 / Math.PI));
        // Fix to 45 degree increments if snapToGrid is on
        if (snapToGrid) {
            if (deg % 45 !== 0)
                deg = Math.round(deg / 45) * 45;
        }
        // Amend shape SVG element for live-preview
        const shapeElement = document.querySelector(`.draggableShape-${itemIdx}`) as SVGSVGElement;
        const controlHandles = document.querySelector(`#shapeControlHandles`);
        if (shapeElement && shapeElement.firstChild) {
            const gEl = shapeElement.firstChild as SVGGElement;
            // transform origin should be the center of the object
            const transformOriginX = shape.width! / 2;
            const transformOriginY = shape.height! / 2;
            gEl.setAttribute("transform", `rotate(${Math.round(deg)}, ${transformOriginX}, ${transformOriginY})`);
        }
        if (controlHandles) {
            (controlHandles.firstChild as SVGGElement).setAttribute("transform", `rotate(${Math.round(deg)})`);
        }
        return deg;
    }

    /** Draws an SVG rectangle as preview for room drawing */
    private _drawRoomLayoutPreviewBox(type: string, initialCoords: number[], mouseX: number, mouseY: number, snapToGrid: boolean) {
        if (this.svg) {
            let room = this.svg.querySelector('#roomDrawing');
            if (!room) {
                room = document.createElementNS(xmlns, "rect") as SVGRectElement;
                room.id = "roomDrawing";
                this.svg.appendChild(room);
            }
            let x1 = Math.min(mouseX, initialCoords[0]);
            let x2 = Math.max(mouseX, initialCoords[0]);
            let y1 = Math.min(mouseY, initialCoords[1]);
            let y2 = Math.max(mouseY, initialCoords[1]);
            if (snapToGrid) {
                if (x1 % 10 !== 0)
                    x1 = Math.round(x1 / 10) * 10;
                if (x2 % 10 !== 0)
                    x2 = Math.round(x2 / 10) * 10;
                if (y1 % 10 !== 0)
                    y1 = Math.round(y1 / 10) * 10 + 2.5;
                if (y2 % 10 !== 0)
                    y2 = Math.round(y2 / 10) * 10 + 2.5;
            }
            room.setAttribute("x", x1.toString());
            room.setAttribute("width", (x2 - x1).toString());
            room.setAttribute("y", y1.toString());
            room.setAttribute("height", (y2 - y1).toString());
            room.setAttribute("fill", type === "DrawFloorplan" ? "white" : "none");
            room.setAttribute("stroke", type === "DrawFloorplan" ? "#999" : "black");
        }
    }

    /** Scrolls the canvas based on the disance between the current mouse position and coordinates captured during initial canvas grab */
    private _performCanvasScroll(e: React.MouseEvent<SVGSVGElement, MouseEvent>, initialCoords: number[]) {
        const deltaX = e.clientX - initialCoords[0];
        const deltaY = e.clientY - initialCoords[1];
        const drawing = document.querySelector('#drawing');
        if (drawing && drawing.firstChild) {
            (drawing.firstChild as HTMLElement).scrollLeft = initialCoords[2] + (deltaX * -1);
            (drawing.firstChild as HTMLElement).scrollTop = initialCoords[3] + (deltaY * -1);
        }
    }

    /** Adds a new stampable element to the canvas, such as a desk or a door - providing there are no collisions */
    private _addNewStampable(mouseX: number, mouseY: number, snapToGrid: boolean, stampables: IStampable[], defaultDeskRotation?: number, offset?: number, options?: {[index: string]: any}) {
        let deskX = mouseX + (offset !== undefined ? offset : -10);
        let deskY = mouseY + (offset !== undefined ? offset : -10);
        if (snapToGrid) {
            if (deskX % 10 !== 0)
                deskX = Math.round(deskX / 10) * 10;
            if (deskY % 10 !== 0)
                deskY = Math.round(deskY / 10) * 10;
        }
        const collision = this._checkDeskCollision(deskX, deskY);
        if (!collision) {
            let stampable: IStampable = {
                x: deskX,
                y: deskY,
                rotation: defaultDeskRotation || 0
            };
            if (options) {
                for (let key in options) {
                    (stampable as any)[key] = options[key];
                }
            }
            (stampables as any).push(stampable);
        }
        return stampables;
    }

    private _commitRotation(type: StampObj, itemIdx: number, mouseX: number, mouseY: number, snapToGrid: boolean) {
        let deg: number = 0; let arrName = "floorplanShapes";
        switch (type) {
            case "Desk":
                deg = this._performDeskRotation(this.state.drawing.desks, itemIdx, mouseX, mouseY, snapToGrid);
                arrName = "desks";
                break;
            case "Door":
                deg = this._performDoorRotation(this.state.drawing.doors, itemIdx, mouseX, mouseY, snapToGrid);
                arrName = "doors";
                break;
            case "Floor":
            case "Room":
                deg = type === "Floor" ? this._performFloorRotation(itemIdx, mouseX, mouseY, snapToGrid) : this._performRoomRotation(itemIdx, mouseX, mouseY, snapToGrid);
                arrName = type === "Floor" ? "floorplanRectangles" : "roomRectangles";
                break;
            case "Shape":
                deg = this._performShapeRotation(itemIdx, mouseX, mouseY, snapToGrid);
                arrName = "floorplanShapes";
                break;
        }
        const amendedObject = { ...(this.state.drawing as any)[arrName][itemIdx] };
        amendedObject.rotation = deg;
        const amendedObjects = [...(this.state.drawing as any)[arrName]];
        amendedObjects[itemIdx] = amendedObject;
        const newState = cloneDeep(this.state);
        (newState.drawing as any)[arrName] = amendedObjects;
        this.setState(newState, () => {
            this._mouseDownOperation = null;
        });
    }

    /** Saves desk rotation to state */
    private _commitDeskRotation(desks: IDesk[], itemIdx: number, mouseX: number, mouseY: number, snapToGrid: boolean) {
        const { drawing } = this.state;
        const desk = desks[itemIdx];
        mouseX = mouseX - (desk.x + 10);
        mouseY = mouseY - (desk.y + 10);
        const a = Math.atan2(mouseY, mouseX);
        let deg = a * (180 / Math.PI);
        const deskElement = document.querySelector('.draggableDesk-' + itemIdx);
        if (deskElement && deskElement.firstChild) {
            if (snapToGrid) {
                if (deg % 45 !== 0)
                    deg = Math.round(deg / 45) * 45;
            }
            const amendedDesk = { ...desks[itemIdx] };
            const amendedDesks = [...desks];
            amendedDesk.rotation = deg;
            amendedDesks[itemIdx] = amendedDesk;
            this.setState({
                ...this.state,
                drawing: {...drawing, desks: amendedDesks},
            }, () => {
                this._mouseDownOperation = null;
            });
        }
        return { mouseX, mouseY };
    }

    /** Saves desk rotation to state */
    private _commitDoorRotation(doors: IStampable[], itemIdx: number, mouseX: number, mouseY: number, snapToGrid: boolean) {
        const { drawing } = this.state;
        const door = doors[itemIdx];
        mouseX = mouseX - (door.x + 10);
        mouseY = mouseY - (door.y + 10);
        const a = Math.atan2(mouseY, mouseX);
        let deg = a * (180 / Math.PI);
        const doorElement = document.querySelector('.draggableDoor-' + itemIdx);
        if (doorElement && doorElement.firstChild) {
            if (snapToGrid) {
                if (deg % 45 !== 0)
                    deg = Math.round(deg / 45) * 45;
            }
            const amendedDoor = { ...doors[itemIdx] };
            const amendedDoors = [...doors];
            amendedDoor.rotation = deg;
            amendedDoors[itemIdx] = amendedDoor;
            this.setState({
                ...this.state,
                drawing: {...drawing, doors: amendedDoors},
            }, () => {
                this._mouseDownOperation = null;
            });
        }
        return { mouseX, mouseY };
    }

    private _commitMovedObject(type: MouseDownOperationType, itemIdx: number) {
        const { drawing } = this.state;
        
        const draggableType = type.replace("Move", "");
        let arrayName = "floorplanShapes";
        switch (draggableType) {
            case "Desk":
                arrayName = "desks";
                break;
            case "Door":
                arrayName = "doors";
                break;
            case "Text":
                arrayName = "text";
                break;
            case "Floor":
                arrayName = "floorplanRectangles";
                break;
            case "Room":
                arrayName = "roomRectangles";
                break;
        }
        let draggableArr: IStampable[] = [...(drawing as any)[arrayName]];
        
        const element = document.querySelector(`.draggable${draggableType}-${itemIdx}`);
        if (draggableArr && element) {
            const x = element.getAttribute("x"), y = element.getAttribute("y");
            if (x && y) {
                const amendedObject = { ...draggableArr[itemIdx] };
                const amendedObjects = [...draggableArr];
                amendedObject.x = parseFloat(x);
                amendedObject.y = parseFloat(y);
                amendedObjects[itemIdx] = amendedObject;
                const newState = cloneDeep(this.state);
                (newState.drawing as any)[arrayName] = amendedObjects;
                this.setState(newState, () => {
                    this._mouseDownOperation = null;
                });
            }
        }
    }

    /** Saves new desk location to state */
    private _commitMovedDesk(itemIdx: number, desks: IDesk[]) {
        const { drawing } = this.state;
        const deskElement = document.querySelector('.draggableDesk-' + itemIdx);
        if (deskElement) {
            const x = deskElement.getAttribute("x"), y = deskElement.getAttribute("y");
            if (x && y) {
                const amendedDesk = { ...desks[itemIdx] };
                const amendedDesks = [...desks];
                amendedDesk.x = parseFloat(x);
                amendedDesk.y = parseFloat(y);
                amendedDesks[itemIdx] = amendedDesk;
                this.setState({
                    ...this.state,
                    drawing: {...drawing, desks: amendedDesks},
                }, () => {
                    this._mouseDownOperation = null;
                });
            }
        }
    }

    /** Saves new door location to state */
    private _commitMovedDoor(itemIdx: number, doors: IStampable[]) {
        const { drawing } = this.state;
        const doorElement = document.querySelector('.draggableDoor-' + itemIdx);
        if (doorElement) {
            const x = doorElement.getAttribute("x"), y = doorElement.getAttribute("y");
            if (x && y) {
                const amendedDoor = { ...doors[itemIdx] };
                const amendedDoors = [...doors];
                amendedDoor.x = parseFloat(x);
                amendedDoor.y = parseFloat(y);
                amendedDoors[itemIdx] = amendedDoor;
                this.setState({
                    ...this.state,
                    drawing: {...drawing, doors: amendedDoors},
                }, () => {
                    this._mouseDownOperation = null;
                });
            }
        }
    }

    /** Updates zoom level */
    private _setZoom(zoom: number) {
        super.setState({
            ...this.state,
            zoom
        });
    }

    /** Handles selection of editor tools */
    private _toolSelection(tool: ToolType) {
        return (e: any) => {
            this._mouseDownOperation = null;
            const cursor = document.querySelector('#cursor');
            if (cursor) {
                cursor.remove();
            }
            super.setState({
                ...this.state,
                selectedDeskIdx: undefined,
                selectedDoorIdx: undefined,
                selectedTool: tool
            }, () => {
                if (this.svg) {
                    this.svg.classList.remove("pointerTool");
                    this.svg.classList.remove("penTool");
                    this.svg.classList.remove("deskTool");
                    this.svg.classList.remove("floorTool");
                    this.svg.classList.remove("roomTool");
                    this.svg.classList.remove("doorTool");
                    this.svg.classList.remove("eraserTool")
                    this.svg.classList.add(tool.toLowerCase() + "Tool");
                    this._triggerMouseMove();
                }
            });
        }
    }

    /** Inverts boolean properties in state */
    private _toggleStateBool(bool: string) {
        return () => {
            const newState = {...this.state};
            (newState as any)[bool] = !(newState as any)[bool];
            super.setState(newState);
        };
    }

    /** Uses getBoundingClientRect to attempt to fit all added desks to the current view */
    private _fitDesksToScreen() {
        super.setState({
            ...this.state,
            zoom: 0.5,
        }, () => {
            const desks = document.querySelector('#desks') as SVGGElement;
            const drawing = document.querySelector('#drawing > div') as HTMLDivElement;
            if (this.svg && desks && drawing) {
                let bounds = desks.getBoundingClientRect();
                let ratio = 0.5;
                if (window.innerWidth > window.innerHeight) {
                    ratio = bounds.height / window.innerHeight;
                } else {
                    ratio = bounds.width / window.innerWidth;
                }
                super.setState({
                    ...this.state,
                    zoom: ratio
                }, () => {
                    bounds = desks.getBoundingClientRect();
                    const wDiff = (window.innerWidth - bounds.width) / 2;
                    const hDiff = (window.innerHeight - bounds.height) / 2;
                    drawing.scrollLeft = bounds.left - wDiff;
                    drawing.scrollTop = bounds.top - hDiff;
                });
            }
        });
    }

    /** Initialises Office UI Icons and adds custom icon registrations */
    private _setupIcons() {
        initializeIcons();
        registerIcons({
            icons: {
                'pointer-svg': (
                    <svg style={{ pointerEvents: "all", cursor: "pointer" }} id="pointer" width="22" height="20" version="1.1" baseProfile="full" viewBox="0 0 22 20" xmlns="http://www.w3.org/2000/svg">
                        <g transform="matrix(1.3101052,0,0,1.3101052,-6.2175751,-9.0274217)">
                            <rect x="12.5" y="13.6" transform="matrix(0.9221,-0.3871,0.3871,0.9221,-5.7605,6.5909)" width="2" height="8" />
                            <polygon points="12.2,15.6 12.6,15.5 17.4,15.5 9.2,7.3 9.2,18.5" />
                        </g>
                    </svg>
                ),
                'pen-svg': (
                    <svg style={{ pointerEvents: "all", cursor: "pointer" }} id="pen" width="22" height="20" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 116.28 122.88">
                        <g>
                            <path
                                style={{ fillRule: "evenodd", clipRule: "evenodd" }}
                                d="M42.85,22.45L77.49,8l29.87,30.77L93.42,73.3c-23.64,6.55-45.66,16.11-66.45,27.96l-4.22-5.24l41.23-40.88 c0.83,0.38,1.73,0.57,2.64,0.57c0,0,0.01,0,0.01,0c3.56,0,6.44-2.88,6.44-6.44c0-3.55-2.88-6.44-6.44-6.44 c-3.55,0-6.44,2.88-6.44,6.44c0,1.15,0.3,2.23,0.83,3.16L20.22,92.97l-5.66-5.17C27.01,67.42,36.53,45.69,42.85,22.45L42.85,22.45z M87.36,0l28.93,29.64l-2.64,2.57l-2.64,2.57L82.08,5.13l2.64-2.57L87.36,0L87.36,0z M18.99,107.36 c13.56,12.48,21.54,7.04,32.83-0.68c9.34-6.38,20.63-14.09,37.75-16.64c1.22-4.1,5.02-7.09,9.51-7.09c5.48,0,9.92,4.44,9.92,9.92 c0,5.48-4.44,9.92-9.92,9.92c-3.85,0-7.19-2.19-8.83-5.4c-15.32,2.43-25.69,9.51-34.28,15.37c-14.47,9.88-24.58,16.78-42.3-0.24 c-1.19,0.48-2.46,0.73-3.74,0.73c0,0-0.01,0-0.01,0c-5.48,0-9.92-4.44-9.92-9.92c0-5.48,4.44-9.92,9.92-9.92 c5.48,0,9.92,4.44,9.92,9.92c0,0,0,0.01,0,0.01C19.84,104.73,19.55,106.09,18.99,107.36L18.99,107.36L18.99,107.36z" />
                        </g>
                    </svg>
                ),
                'desk-svg': (
                    <svg style={{ pointerEvents: "all", cursor: "pointer", alignSelf: "center" }} id="deskTool" width="20" height="20" version="1.1" baseProfile="full" viewBox="0 0 20 20" xmlns="http://www.w2.org/2000/svg">
                        <g>
                            <rect ry="3" rx="3" y="2" x="0" height="17" width="20" style={{
                                fill: "#cccccc", fillOpacity: 1, stroke: "none", strokeWidth: 0.35, strokeMiterlimit: 4, strokeDasharray: "none"
                            }} />
                            <ellipse ry="7" rx="7" cy="7.5" cx="10" style={{
                                fill: "#ffffff", fillOpacity: 1, stroke: "#808080", strokeWidth: 1.1, strokeMiterlimit: 4, strokeDasharray: "none"
                            }} />
                        </g>
                    </svg>
                )
            }
        });
    }

    /** Given a reference to the editor's SVG Canvas and a MouseEvent - converts the mouse coordinates of the event to coordinates in SVG space */
    private _getSvgMousePosition(e: React.MouseEvent, svg: SVGSVGElement | null) {
        let x, y; 
        if (svg) {
            const ctm = svg.getScreenCTM();
            if (ctm) {
                x = (e.clientX - ctm.e) / ctm.a;
                y = (e.clientY - ctm.f) / ctm.d;
            }
        }
        return {x,y};
    }

    /** Given SVG coordinates x/y, determines any selections the user may have made */
    private _checkSelection(xSvg: number, ySvg: number, xMouse: number, yMouse: number) {
        const { desks, doors, roomRectangles, floorplanRectangles, floorplanShapes } = this.state.drawing;
        let returnObj: Record<string, number|undefined> = {
            clickedDeskIdx: undefined,
            clickedDoorIdx: undefined,
            clickedTextIdx: undefined,
            clickedFloorIdx: undefined,
            clickedRoomIdx: undefined,
            clickedShapeIdx: undefined
        };
        const boundsChecker: Record<string, (IStampable|IRoomRectangle)[]> = { Floor: floorplanRectangles, Shape: floorplanShapes, Room: roomRectangles, Door: doors, Desk: desks };
        for (let key in boundsChecker) {
            boundsChecker[key].forEach((item, idx) => {
                if (item.x != null && item.y != null) {
                    let bounds: { left: number, right: number, top: number, bottom: number} | undefined = undefined;
                    if (item.width != null && item.height != null) {
                        bounds = { top: item.y, bottom: item.y + (item as IRoomRectangle).height, left: item.x, right: item.x + (item as IRoomRectangle).width };
                    } else {
                        bounds = { top: item.y, bottom: item.y + 20, left: item.x, right: item.x + 20 };
                    }
                    if (bounds &&
                        xSvg >= bounds.left &&
                        xSvg <= bounds.right &&
                        ySvg >= bounds.top &&
                        ySvg <= bounds.bottom
                    ) {
                        returnObj[`clicked${key}Idx`] = idx;
                    }
                }
            });
        }
        if (this.svg) {
            const element = document.elementFromPoint(xMouse, yMouse);
            console.log(element);
            if (element && (element.nodeName === "text" || element.nodeName === "tspan")) {
                let parentElement: Node | null = element as SVGElement;
                while (parentElement !== null && parentElement.nodeName !== "svg") {
                    parentElement = parentElement.parentNode;
                }
                if (parentElement) {
                    const idxStr = (parentElement as SVGSVGElement).getAttribute("data-idx");
                    if (idxStr) {
                        const idx = parseInt(idxStr, 10);
                        returnObj.clickedTextIdx = idx;
                    }
                }
            }
        }
        return returnObj;
    }

    /** Given proposed desk coordinates x/y, determines if this new desk would intersect with another. 
     * Collision detection algorithm used to prevent users from putting desks on top of one another. */
    private _checkDeskCollision(x: number, y: number) {
        const { desks } = this.state.drawing;
        let boundsA = { top: y, bottom: y + 20, left: x, right: x + 20 };
        let collision = false;
        desks.forEach((desk) => {
            let boundsB = { top: desk.y, bottom: desk.y + 20, left: desk.x, right: desk.x + 20 };
            if (
                boundsA.top < boundsB.bottom && 
                boundsA.bottom > boundsB.top &&
                boundsA.left < boundsB.right && 
                boundsA.right > boundsB.left
            ) {
                collision = true;
            }
        });
        return collision;
    }

    /** Performs previously undone state changes */
    private redo() {
        const { floorplanId, layoutId } = this.props;
        const { undoStates, redoStates } = this.state;
        const redos = [...redoStates];
        if (redos.length >= 1) {
            const newUndoState: IRoomEditorDrawingState = {...this.state.drawing};
            const newDrawingState = redos.pop();
            if (newDrawingState) {
                newDrawingState.lastUpdate = new Date().toISOString();
                localStorage.setItem(`draft_${floorplanId}_${layoutId}`, JSON.stringify(newDrawingState));
                super.setState({
                    ...this.state,
                    drawing: newDrawingState ? {...newDrawingState, tempShape: []} : this._initialDrawingState,
                    undoStates: [...undoStates, newUndoState],
                    redoStates: redos,
                }, () => {
                    this._mouseDownOperation = null;
                    if (this.svg) {
                        const room = this.svg.querySelector('#roomDrawing');
                        if (room) room.remove();
                    }
                });
            }
        }
    }

    /** Rewinds recently completed state changes */
    private undo() {
        const { floorplanId, layoutId } = this.props;
        const { undoStates, redoStates } = this.state;
        const undos = [...undoStates];
        if (this._penInUse) {
            super.setState({
                ...this.state,
                drawing: {
                    ...this.state.drawing,
                    tempShape: []
                }
            }, () => {
                this._penInUse = false;
                this._mouseDownOperation = null;
            });
            return;
        }
        if (undos.length >= 1) {
            const newRedoState: IRoomEditorDrawingState = {...this.state.drawing};
            const newDrawingState = undos.pop();
            if (newDrawingState) {
                newDrawingState.lastUpdate = new Date().toISOString();
                localStorage.setItem(`draft_${floorplanId}_${layoutId}`, JSON.stringify(newDrawingState));
                super.setState({
                    ...this.state,
                    drawing: newDrawingState ? {...newDrawingState, tempShape: [] } : this._initialDrawingState,
                    undoStates: undos,
                    redoStates: [...redoStates, newRedoState]
                }, () => {
                    this._mouseDownOperation = null;
                    if (this.svg) {
                        const room = this.svg.querySelector('#roomDrawing');
                        if (room) room.remove();
                    }
                });
            }
        }
    }

    /** Handles keyboard input for the editor, capturing combination key presses */
    private _handleKeyDown(e: KeyboardEvent) {
        if (e.key === "Escape" && this._penInUse) {
            super.setState({
                ...this.state,
                drawing: {
                    ...this.state.drawing,
                    tempShape: []
                }
            }, () => {
                this._penInUse = false;
                this._mouseDownOperation = null;
            });
            return;
        }
        if (e.ctrlKey && e.key === "z") {
            this.undo();
        }
        if (e.ctrlKey && e.key === "y") {
            this.redo();
        }
    }

    /** Handles keyboard input for the editor */
    private _handleKeyUp(e: KeyboardEvent) {
        if (this.svg && this.state.selectedTextIdx !== undefined) {
            // const activeTextBox = this.svg.querySelector('.draggableText-' + this.state.selectedTextIdx);
            // if (activeTextBox) {
            //     const textElement = activeTextBox.querySelector('text');
            //     if (textElement) {
            //         let text = textElement.textContent;
            //         if (e.key === "Backspace") {
            //             text = text ? text.substr(0,text.length - 1) : "";
            //         } else if (e.key === "Enter") {
            //             text += "\n";
            //         } else if (e. /[a-zA-Z0-9:-\s]/.test(e.key)) {
            //             text += e.key;
            //         }
            //         textElement.textContent = text;
            //     }
            // }
        } else {
            if (e.key === "v") {
                this._toolSelection("Pointer")(null);
            }
            if (e.key === "d") {
                this._toolSelection("Desk")(null);
            }
            if (e.key === "t") {
                super.setState({
                    ...this.state,
                    availability: !this.state.availability
                }, this._triggerMouseMove);
            }
            if (e.key === "i") {
                if (this.state.selectedTool === "Room" || this.state.selectedTool === "Floor") {
                    super.setState({
                        ...this.state,
                        selectedTool: this.state.selectedTool === "Room" ? "Floor" : "Room"
                    }, this._triggerMouseMove);
                }
            }
            if (e.key === "g") {
                super.setState({
                    ...this.state,
                    showGrid: !this.state.showGrid
                }, this._triggerMouseMove);
            }
            if (e.key === "s") {
                super.setState({
                    ...this.state,
                    snapToGrid: !this.state.snapToGrid
                }, this._triggerMouseMove);
            }
            if (e.key === "r") {
                let { defaultDeskRotation } = this.state;
                defaultDeskRotation += 45;
                if (defaultDeskRotation >= 360) defaultDeskRotation = 0;
                super.setState({
                    ...this.state,
                    defaultDeskRotation,
                }, this._triggerMouseMove);
            }
        }
    }

    /** Synthetically triggers a mousemove event on the SVG Canvas. 
     * Used to programatically update mouseover previews when tools and other settings change. */
    private _triggerMouseMove() {
        if (this.svg) {
            this.svg.dispatchEvent(new MouseEvent("mousemove", {
                view: window,
                bubbles: true,
                cancelable: true,
                clientX: this._liveX || 0,
                clientY: this._liveY || 0
            }));
        }
    }
}

export default withRouter(RoomEditor);